// The extra case details sent to the adviser in the enquiry's comments:
// none of these are matched on, they help the adviser pick the case up.
// Pure functions (comments.test.ts).
import { formatPct, money } from '../format';

export interface RefurbDetails {
  propertyValueGbp: number;
  purchaseCostsPct: number;
  purchaseCostsGbp: number;
  alreadyOwned: boolean;
  buildCosts: number;
  professionalCosts: number;
  contingencyPct: number;
  gdv: number;
}

export function refurbTotalGbp(d: Pick<RefurbDetails, 'buildCosts' | 'professionalCosts'>): number {
  return d.buildCosts + d.professionalCosts;
}

export function contingencyGbp(d: Pick<RefurbDetails, 'buildCosts' | 'professionalCosts' | 'contingencyPct'>): number {
  return Math.round(refurbTotalGbp(d) * d.contingencyPct / 100);
}

/** '' when fine; otherwise why the GDV can't be right (it has to be more than the purchase price). */
export function gdvError(isRefurb: boolean, gdv: number, propertyValueGbp: number): string {
  if (!isRefurb || gdv <= 0 || gdv > propertyValueGbp) return '';
  return `GDV must be more than the purchase price (${money(propertyValueGbp)}).`;
}

export function refurbCommentBlock(d: RefurbDetails): string {
  return [
    'Refurbishment details:',
    d.alreadyOwned ? 'Purchase costs: n/a (property already owned by the client)' : `Purchase costs: ${formatPct(d.purchaseCostsPct)}% (${money(d.purchaseCostsGbp)})`,
    `Build costs: ${money(d.buildCosts)}`,
    `Professional costs: ${money(d.professionalCosts)}`,
    `Total refurbishment costs: ${money(refurbTotalGbp(d))}`,
    `Contingency: ${formatPct(d.contingencyPct)}% (${money(contingencyGbp(d))})`,
    `GDV: ${d.gdv > 0 ? money(d.gdv) : 'not given'}`,
  ].join('\n');
}

/** Second charge: the first charge balance, with the combined LTV. '' when not given. */
export function firstChargeCommentBlock(firstChargeGbp: number, loanGbp: number, propertyValueGbp: number): string {
  if (!(firstChargeGbp > 0)) return '';
  const combined = propertyValueGbp > 0 ? ` (combined LTV with this loan: ${(((firstChargeGbp + loanGbp) / propertyValueGbp) * 100).toFixed(1)}%)` : '';
  return `First charge outstanding: ${money(firstChargeGbp)}${combined}`;
}

export interface PortfolioProperty { address: string; value: number }

/** Mixed portfolio: every security property entered, with a total. '' when none were entered. */
export function portfolioCommentBlock(propertyTypeLabel: string, properties: PortfolioProperty[], propertyValueGbp: number): string {
  const entered = properties.filter((p) => p.address || p.value > 0);
  if (entered.length === 0) return '';
  return [
    `Security properties (${propertyTypeLabel}):`,
    ...entered.map((p, i) => `${i + 1}. ${p.address || 'Address not given'} — ${p.value > 0 ? money(p.value) : 'value not given'}`),
    `Total of values entered: ${money(entered.reduce((sum, p) => sum + p.value, 0))} (quick estimate property value: ${money(propertyValueGbp)})`,
  ].join('\n');
}
